import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import char from '../../assets/images/intake/step10/char.svg';
import { useEmploymentReadinessContext } from '../../hooks/use-form-context.hooks';
import { dispatchUpdateEmploymentReadiness } from '../../redux/slice/form-employment-readiness';
import { ProfessionalResourceStatus } from '../../redux/slice/form-employment-readiness/form-employment-readiness.types';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface FormData {
  professionalResume: string;
}
interface ComponentProps {
  step: number;
  onBack: () => void;
}
const resume_options = [
  {
    label: 'What is a resume?',
    value: ProfessionalResourceStatus.WHAT_IS_IT,
  },
  {
    label: 'No',
    value: ProfessionalResourceStatus.NO,
  },
  {
    label: "I have one that's not complete",
    value: ProfessionalResourceStatus.INCOMPLETE,
  },
  {
    label: "I have a resume but not sure if it's professional",
    value: ProfessionalResourceStatus.UNPROFESSIONAL,
  },
  {
    label: 'Yes, I have a professional resume',
    value: ProfessionalResourceStatus.YES,
  },
];
export const CandidateMarketingToolkit: React.FC<ComponentProps> = ({ step, onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { professionalResume } = useEmploymentReadinessContext();
  const [selectedResumeOption, setSelectedResumeOption] = useState(professionalResume || '');
  const [error, setError] = useState('');
  const handleResumeOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedResumeOption(event.target.value);
    setError('');
  };
  const { handleSubmit } = useForm<FormData>();
  const submitWorkHistory: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted professional resume option.', selectedResumeOption);

    if (!selectedResumeOption) {
      setError('Please select an option');
    } else {
      const dto: Record<keyof FormData, ProfessionalResourceStatus> = {
        professionalResume: selectedResumeOption as ProfessionalResourceStatus,
      };
      await dispatch(dispatchUpdateEmploymentReadiness(dto));
      navigate(`/pre-employment/employment-readiness/step${step + 1}`);
    }
  };
  const backButtonClick = () => {
    onBack();
  };

  useEffect(() => {
    if (professionalResume) {
      setSelectedResumeOption(professionalResume);
    }
  }, [professionalResume]);

  return (
    <div>
      <div>
        <p className="text-lg mt-7 md:px-10">
          In this section, we'll delve into the world of self-marketing, a crucial skill for any
          aspiring professional. Let's start with understanding your self-marketing proficiency
          through check-off and multiple-choice style questions.
        </p>
        <p className="text-lg mt-7 md:px-10">
          These questions will help you evaluate your current standing in terms of self-marketing
          materials. Don't worry; we're here to guide you through every step of the way.
        </p>
        <p className="text-lg mt-7 md:px-10">
          <span className="font-semibold">Self-marketing package: </span>
          including sample cover letters, resume, profile, job interview skills.
        </p>
      </div>
      <div className="flex items-center mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">11</div>
        <div>
          <h4 className="text-2xl">
            I have an updated
            <span className="font-semibold"> professional sample resume</span>
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 right-[-125px] z-[-1]">
        <img className="" src={char} alt="" />
      </div>
      <form
        className="md:px-10"
        action=""
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitWorkHistory)}
      >
        <div className="grid gap-y-3">
          {resume_options.map((option) => (
            <div key={option.label} className="h-[60px]">
              <label className="radio-container h-full p-4 block relative">
                <input
                  checked={selectedResumeOption === option.value}
                  onChange={handleResumeOptionChange}
                  value={option.value}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  name="resume option"
                />
                <span className="checkmark !block h-auto pt-4">{option.label}</span>
              </label>
            </div>
          ))}
          {error && <p className="text-red-500">{error}</p>}
        </div>
        <div className="flex justify-between mt-20">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
